import { useEffect, useState } from 'react';
import { IC, Svg } from '../art/icons.js';
import { fmt, readyWhen } from '../lib/format.js';
import { collectLine, groupApi, type GroupView } from '../lib/group.js';
import { useNav } from '../lib/router.js';
import { orderingOpen, useStore } from '../lib/store.js';
import { OrderingClosed, Scroll, TopBar } from '../components/ui.js';
import { pickupText } from '../components/Sheets.js';
import { track } from '../lib/events.js';
import './group.css';

/**
 * The Order tab's Group order tile (docs/28, Saif 22 Oct 2026): the host picks a collection time, starts the group and
 * gets the link. Groups already on the go are listed first, so a host can get back to one.
 */
export function GroupStart() {
  const s = useStore();
  const { navigate } = useNav();
  const u = s.me?.customer ?? null;
  const [mine, setMine] = useState<GroupView[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const signedOut = s.meLoaded && !s.me;
  useEffect(() => {
    if (signedOut) navigate('/join?next=/group', { replace: true });
  }, [signedOut]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (u) groupApi.mine().then(setMine).catch(() => setMine([]));
  }, [u?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const g = await groupApi.start(s.pickup);
      track('group_started', { groupId: g.id });
      navigate(`/g/${g.token}`, { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start the group order');
      setBusy(false);
    }
  };
  return (
    <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
      <TopBar title="Group order" />
      <OrderingClosed />
      {mine && mine.length > 0 && (
        <section className="col" style={{ gap: 10 }}>
          <h2 className="h3">On the go</h2>
          <div className="card rows">
            {mine.map((g) => <button type="button" key={g.id} onClick={() => navigate(`/g/${g.token}`)}><span className="ic"><Svg html={IC.people} /></span><span className="grow col" style={{ gap: 2, minWidth: 0 }}><b>{collectLine(g, readyWhen)} · {g.people.length} {g.people.length === 1 ? 'person' : 'people'}</b><span className="s">{g.state === 'open' ? `Open · ${g.lines.reduce((a, l) => a + l.qty, 0)} items so far · ${fmt(g.totalPence)}` : g.state === 'locked' ? `Locked · ${fmt(g.totalPence)} to pay` : g.state === 'expired' ? `Closed for adding · ${fmt(g.totalPence)} to pay` : 'Being paid for'}</span></span><Svg html={IC.chev} /></button>)}
          </div>
        </section>
      )}
      <div className="card col" style={{ padding: 18, gap: 10 }}>
        <span style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--accent-soft)', color: 'var(--accent-text)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Svg html={IC.people} /></span>
        <h2 className="display" style={{ fontSize: 26, lineHeight: 1.1, margin: 0 }}>One link, everyone adds their own, you pay once</h2>
        <p className="muted" style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>Share the link with your friends. Each of them picks their own bowl, wrap or sides, in the app or on the website, no account needed. You see it all arrive, then pay for the lot as one order. One pickup, one code, every bag with a name on.</p>
        <p className="small muted" style={{ margin: 0 }}>Friends who are signed in earn their own points on what they add. You earn points on the rest.</p>
      </div>
      <section className="col" style={{ gap: 10 }}><h2 className="h3">When will you collect?</h2>
        <button type="button" className="card pickline" style={{ background: 'var(--surface)', padding: '14px 16px' }} onClick={() => s.openSheet({ kind: 'pickup' })}><span><Svg html={IC.clock} /></span><span className="grow col" style={{ gap: 1, minWidth: 0 }}><b>{pickupText(s)}</b><span className="s">{s.config?.site.address}</span></span><span className="chg">Change</span></button>
        <span className="small muted">You can change the time when you pay. The group stays open for adding for about two hours, or until you lock it.</span>
      </section>
      {err && <p className="err" role="alert" style={{ margin: 0 }}>{err}</p>}
      {orderingOpen(s) && <button type="button" className="yellow" disabled={busy || !u} onClick={start}>{busy ? 'One moment…' : 'Start and get the link'}</button>}
    </Scroll>
  );
}
